import { useEffect, useMemo, useState } from "react";
import { Camera, Loader2, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";

// Phase 44.12 — Sanity threshold: foto reali ≥ ~1KB di base64. Sotto questa
// soglia consideriamo l'allegato un placeholder/seed-stub (es. 1×1 transparent
// PNG dal demo seed) e mostriamo l'empty-state pulito invece di un'icona rotta.
const MIN_BASE64_BYTES = 1024;

export default function DeliveryPhotoView({ orderId, testid = "delivery-photo" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  useBodyScrollLock(fullscreen);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/orders/${orderId}/delivery-photo`);
        if (!cancel) setData(d);
      } catch (e) {
        if (!cancel) setErr(formatApiError(e.response?.data?.detail) || "");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [orderId]);

  const url = useMemo(() => {
    const b = (data?.data_base64 || "").trim();
    if (!b || b.length < MIN_BASE64_BYTES) return null;
    return b.startsWith("data:") ? b : `data:image/jpeg;base64,${b}`;
  }, [data?.data_base64]);

  if (loading) {
    return (
      <div className="card-soft p-5 sm:p-6" data-testid={`${testid}-loading`}>
        <p className="overline inline-flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> Foto consegna</p>
        <p className="mt-2 text-sm text-dapnow-muted inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</p>
      </div>
    );
  }
  if (err || !url || imgFailed) {
    return (
      <div className="card-soft p-5 sm:p-6" data-testid={`${testid}-empty`}>
        <p className="overline inline-flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> Foto consegna</p>
        <p className="mt-2 text-sm text-dapnow-muted">
          La consegna è stata confermata dal trasportatore. Per questo ordine non è stata caricata una foto di prova.
        </p>
      </div>
    );
  }
  return (
    <>
      <div className="card-soft p-5 sm:p-6 space-y-3" data-testid={testid}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="overline inline-flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> Foto consegna</p>
          {data.delivered_at && (
            <span className="text-xs text-dapnow-muted">{formatDateTime(data.delivered_at)}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setFullscreen(true)}
          className="block w-full rounded-2xl overflow-hidden border border-dapnow-line focus:outline-none focus:ring-2 focus:ring-dapnow-blue active:opacity-90 transition"
          data-testid={`${testid}-thumb`}
          aria-label="Ingrandisci la foto della consegna"
        >
          <img
            src={url}
            alt="Foto consegna"
            data-testid={`${testid}-img`}
            onError={() => setImgFailed(true)}
            loading="lazy"
            className="w-full max-h-96 object-cover"
          />
        </button>
        {data.delivered_by_name && (
          <p className="text-xs text-dapnow-muted" data-testid={`${testid}-driver`}>
            Consegnato da <strong className="text-dapnow-ink">{data.delivered_by_name}</strong>
          </p>
        )}
        {data.delivery_note && (
          <p className="text-sm text-dapnow-ink bg-dapnow-bg rounded-xl p-3" data-testid={`${testid}-note`}>
            “{data.delivery_note}”
          </p>
        )}
      </div>

      {fullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
          role="dialog"
          aria-modal="true"
          onClick={() => setFullscreen(false)}
          data-testid={`${testid}-modal`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFullscreen(false); }}
            className="absolute top-[calc(env(safe-area-inset-top)+1rem)] right-4 inline-flex items-center justify-center h-11 w-11 rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20 transition"
            aria-label="Chiudi"
            data-testid={`${testid}-modal-close`}
          >
            <XCircle className="h-6 w-6" />
          </button>
          <img
            src={url}
            alt="Foto consegna (ingrandita)"
            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
